const PRODUCTION_BRAIN_DOMAIN = 'brain.gridwatchems.com';

/** `qa` must be a whole label directly before `brain` — a substring test reads tenant `qatar` as QA. */
function isQaHost(host: string): boolean {
  const labels = host.split('.');
  return labels.some((label, index) => label === 'brain' && labels[index - 1] === 'qa');
}

/**
 * Onboarding setup UI (portal /onboarding routes, sidebar entry, SSE, preload).
 * Enabled on QA/local and production Brain hosts.
 */
export function isOnboardingSetupEnabled(host = window.location.hostname): boolean {
  return (
    isQaHost(host) ||
    host === 'localhost' ||
    host.startsWith('127.0.0.1') ||
    isProductionBrainPortal(host)
  );
}

/**
 * Auto-start SSE + cache warmup for every portal page load.
 * QA/local only — on prod, deep-link testers start these from the onboarding page itself
 * so regular users are not hit with onboarding background traffic.
 */
export function shouldAutoStartOnboardingShell(host = window.location.hostname): boolean {
  return isQaHost(host) || host === 'localhost' || host.startsWith('127.0.0.1');
}

/** True on production Brain tenant hosts (not QA). */
export function isProductionBrainPortal(host = window.location.hostname): boolean {
  return (host === PRODUCTION_BRAIN_DOMAIN || host.endsWith(`.${PRODUCTION_BRAIN_DOMAIN}`)) && !isQaHost(host);
}

/**
 * True on QA hosts only (not localhost, not prod). On QA the standalone Utility Bills
 * sidebar row is hidden — the bill inbox lives in Onboarding → Manual Upload there.
 */
export function isQaBrainPortal(host = window.location.hostname): boolean {
  return isQaHost(host);
}

/**
 * M&V (savings report) is a QA/local preview. Production hosts never get the sidebar
 * item or the route, even after this code ships in the prod bundle.
 *
 * The object wrapper is what menu specs stub — ESM `export function` bindings are not
 * writable, so `spyOn(module, 'isMandvPreviewEnabled')` throws in Karma.
 */
export const mandvPreview = {
  isEnabled(hostname = window.location.hostname): boolean {
    return isQaHost(hostname) || hostname === 'localhost' || hostname.endsWith('.localhost') || hostname.startsWith('127.0.0.1');
  },
};

export function isMandvPreviewEnabled(hostname = window.location.hostname): boolean {
  return mandvPreview.isEnabled(hostname);
}
